import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createContext, useEffect } from 'react';
import useDataStore from '../store/useDataStore';
import sha1 from 'crypto-js/sha1';
import { useGroups } from '../hooks/query/ported/useGroups';
import { useApptypes } from '../hooks/query/ported/useApptypes';
import { useResources } from '../hooks/query/useResources';
import { useDates } from '../hooks/query/useDates';
import { useTimes } from '../hooks/query/useTimes';
import { useCategories } from '../hooks/query/useCategories';
import { useProducts } from '../hooks/query/useProducts';
import useWidgetStore from '../store/useWidgetStore';
import ConfigHolder from '../config/ConfigHolder';
const initialState = {};
export const apiSign = (params, api_key, api_salt, body) => {
    var _a, _b;
    let keys = Object.keys(params);
    keys.sort();
    let signatureString = '', excludeKeys = ['api_salt', 'api_key', 'api_signature', 'api_format', 'api_lang', 'api_jsonp_callback'];
    keys.forEach((key) => {
        if (excludeKeys.includes(key))
            return;
        if (params[key] === null)
            params[key] = '';
        signatureString = signatureString.concat(key).concat(params[key]);
    });
    if (body) {
        signatureString = signatureString.concat(body);
    }
    signatureString = signatureString.concat((_b = (_a = process.env) === null || _a === void 0 ? void 0 : _a.YII_API_APPLICATION_ID) !== null && _b !== void 0 ? _b : 'widget-booking').concat(api_salt.toString());
    let hash = sha1(signatureString).toString();
    return hash;
};
export const prepareUrl = (url, method, api_key, widget_id, params) => {
    var _a, _b, _c, _d;
    let api_salt = Date.now();
    url.searchParams.set('api_format', 'json');
    url.searchParams.set('api_lang', 'nl');
    url.searchParams.set('api_salt', api_salt.toString());
    url.searchParams.set('applicationId', (_b = (_a = process.env) === null || _a === void 0 ? void 0 : _a.YII_API_APPLICATION_ID) !== null && _b !== void 0 ? _b : 'widget-booking');
    url.searchParams.set('api_key', api_key);
    url.searchParams.set('widget_id', widget_id);
    let _params = Object.assign(Object.assign({}, params), {
        applicationId: (_d = (_c = process.env) === null || _c === void 0 ? void 0 : _c.YII_API_APPLICATION_ID) !== null && _d !== void 0 ? _d : 'widget-booking',
        method: method,
        widget_id: widget_id
    });
    url.searchParams.set('api_signature', apiSign(_params, api_key, api_salt));
    Object.entries(params).forEach((param) => {
        url.searchParams.set(param[0], param[1]);
    });
    return url;
};
export const WidgetContext = createContext(initialState);
const WidgetProvider = ({ children, initial }) => {
    const { setApptypes, setResources, setCategories, setGroups, setProducts, setDates, setTimes, } = useDataStore();
    const { selectedApptype, enabled, setEnabled } = useWidgetStore();
    const groupQuery = useGroups();
    const apptypeQuery = useApptypes();
    const resourceQuery = useResources();
    const dateQuery = useDates();
    const timeQuery = useTimes();
    const categoryQuery = useCategories();
    const productQuery = useProducts();
    useEffect(() => {
        var _a, _b;
        if (groupQuery.data) {
            let objects = [];
            if (groupQuery.data && ((_b = (_a = groupQuery.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.items.length) > 0) {
                groupQuery.data.result.items.forEach((object) => {
                    var _a, _b, _c;
                    objects.push({
                        id: object.id,
                        name: object.widgetName,
                        subtitle: object.subtitle,
                        description: object.description,
                        image: (_a = object.image) !== null && _a !== void 0 ? _a : '/images/avatar/default.png',
                        duration: (_b = parseInt(object.duration)) !== null && _b !== void 0 ? _b : 0,
                        price: (_c = object.price) !== null && _c !== void 0 ? _c : 0
                    });
                });
            }
            setGroups(objects);
        }
    }, [groupQuery.data, setGroups]);
    useEffect(() => {
        var _a, _b;
        if (apptypeQuery.data) {
            let objects = [];
            if (apptypeQuery.data && ((_b = (_a = apptypeQuery.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.items.length) > 0) {
                apptypeQuery.data.result.items.forEach((object) => {
                    var _a, _b, _c, _d, _e;
                    let minConsumers = 1, maxConsumers = 1, capacityRules = null;
                    if (object.plugins) {
                        let capacityPlugin = object.plugins.find((plugin) => plugin.id === 'multiple');
                        if (capacityPlugin) {
                            minConsumers = capacityPlugin.config.minConsumers;
                            maxConsumers = capacityPlugin.config.maxConsumers;
                            capacityRules = (_b = (_a = capacityPlugin.config) === null || _a === void 0 ? void 0 : _a.rules) !== null && _b !== void 0 ? _b : null;
                            // todo: implement the actual capacity groups, and figure out what singleBooking does on the widget side of things
                        }
                    }
                    // porting
                    // let imageUrl: string = object.image
                    //     ? ConfigHolder.apiBaseUrl.concat(object.image)
                    //     : '/images/avatar/default.png',
                    //     price: number = object.price?.price ?? 0,
                    //     fixedPrice : boolean = object.price?.type === 'fixed'
                    let imageUrl = (_c = object.image) !== null && _c !== void 0 ? _c : '/images/avatar/default.png', price = (_d = object.price) !== null && _d !== void 0 ? _d : 0, fixedPrice = object.fixedPrice;
                    if (!imageUrl.startsWith('https')) {
                        imageUrl = `${ConfigHolder.apiBaseUrl}${imageUrl}`;
                    }
                    objects.push({
                        id: object.id,
                        name: object.name,
                        subtitle: object.subtitle,
                        description: object.description,
                        image: imageUrl,
                        duration: (_e = parseInt(object.duration)) !== null && _e !== void 0 ? _e : 0,
                        price: price,
                        fixedPrice: fixedPrice,
                        parentId: object.groupId,
                        resources: object.allowedResources,
                        minConsumers: minConsumers,
                        maxConsumers: maxConsumers,
                        capacityRules: capacityRules
                    });
                });
            }
            setApptypes(objects);
        }
    }, [apptypeQuery.data, setApptypes]);
    useEffect(() => {
        var _a, _b;
        if (resourceQuery.data) {
            let objects = [];
            if (resourceQuery.data && ((_b = (_a = resourceQuery.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.count) > 0) {
                resourceQuery.data.result.items.forEach((object) => {
                    var _a, _b, _c;
                    objects.push({
                        // id: object.id,
                        id: parseInt(object.id.toString().split('_').shift()),
                        name: object.name,
                        subtitle: object.subtitle,
                        description: object.description,
                        image: (_a = object.image) !== null && _a !== void 0 ? _a : '/images/avatar/default.png',
                        duration: (_b = parseInt(object.duration)) !== null && _b !== void 0 ? _b : 0,
                        price: (_c = object.price) !== null && _c !== void 0 ? _c : 0
                    });
                });
            }
            setResources(objects);
        }
    }, [resourceQuery.data, setResources]);
    useEffect(() => {
        var _a, _b;
        if (dateQuery.data) {
            let objects = [];
            if (dateQuery.data && ((_b = (_a = dateQuery.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.count) > 0) {
                dateQuery.data.result.items.forEach((object) => {
                    var _a;
                    objects.push({
                        id: object.id,
                        time: `${object.date} 00:00`,
                        apptype: object.apptypes,
                        resource: object.resource,
                        duration: (_a = object.duration) !== null && _a !== void 0 ? _a : 0,
                        price: object.price ? object.price[object.apptypes] : 0
                    });
                });
            }
            setDates(objects);
        }
    }, [dateQuery.data, setDates]);
    useEffect(() => {
        var _a, _b;
        if (timeQuery.data) {
            let objects = [];
            if (timeQuery.data && ((_b = (_a = timeQuery.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.count) > 0) {
                timeQuery.data.result.items.forEach((object) => {
                    var _a;
                    let date = object.date.toString(), d = date.substring(6, 8), m = date.substring(4, 6), y = date.substring(0, 4);
                    objects.push({
                        id: object.id,
                        time: `${y}-${m}-${d} ${object.time}`,
                        apptype: object.apptypes,
                        resource: object.resource !== '-1' ? object.resource : object.assignedResource,
                        duration: (_a = object.duration) !== null && _a !== void 0 ? _a : 0,
                        // @ts-ignore
                        price: object.price ? Object.values(object.price).reduce((a, b) => parseFloat(a) + parseFloat(b), 0) : 0,
                        label: object.label ? object.label : object.time.toString()
                    });
                });
            }
            objects.sort((a, b) => {
                return a.id - b.id;
            });
            setTimes(objects);
        }
    }, [timeQuery.data, setTimes]);
    useEffect(() => {
        var _a, _b;
        if (categoryQuery.data) {
            let objects = [];
            if (categoryQuery.data && ((_b = (_a = categoryQuery.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.count) > 0) {
                categoryQuery.data.result.items.forEach((object) => {
                    var _a;
                    objects.push({
                        // id: object.id,
                        id: parseInt(object.id.toString().split('_').shift()),
                        name: object.name,
                        subtitle: object.subtitle,
                        description: object.description,
                        image: (_a = object.image) !== null && _a !== void 0 ? _a : '/modules/consumer/images/photoMissing.jpg',
                        duration: 0,
                        price: 0
                    });
                });
            }
            setCategories(objects);
        }
    }, [categoryQuery.data, setCategories]);
    useEffect(() => {
        var _a, _b;
        if (productQuery.data) {
            let objects = [];
            let varianceId = 1;
            if (productQuery.data && ((_b = (_a = productQuery.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.count) > 0) {
                productQuery.data.result.items.forEach((object) => {
                    var _a, _b, _c;
                    objects.push({
                        id: parseInt(object.id.toString().split('_').shift()),
                        name: object.name,
                        subtitle: object.subtitle, // never set
                        description: object.description, // never set
                        image: (_a = object.image) !== null && _a !== void 0 ? _a : '/modules/consumer/images/photoMissing.jpg',
                        price: parseFloat(object.price),
                        parentId: object.groupId,
                        type: object.type,
                        stock: object.stock,
                        rentalStock: object.rentalStock,
                        trackStock: object.trackStock,
                        required: object.required,
                        requiredApp: object.requiredApp,
                        crossselling: (_b = object.crossselling) !== null && _b !== void 0 ? _b : [],
                        variances: (_c = object.variances) !== null && _c !== void 0 ? _c : [],
                        status: object.status,
                    });
                });
            }
            // parse all variations of products, so we have 1 virtual base product with actual variation products
            let _objects = [], _virtualBaseProductIds = [], _variationProductIds = [];
            objects.map((product) => {
                let productName = product.name, productSubtitle = product.subtitle, productDescription = product.description, productImage = product.image, productId = product.id;
                if (product.name.includes(" |")) {
                    if (_virtualBaseProductIds.includes(productId) || _variationProductIds.includes(product.id)) {
                        return;
                    }
                    if (!_virtualBaseProductIds.includes(productId)) {
                        productName = product.name.substring(0, product.name.indexOf('|') - 1);
                    }
                }
                let _variances = [];
                let _variations = [];
                let _varianceName = '';
                let _testVarianceName = '';
                objects.map((innerProduct) => {
                    if (innerProduct.name.includes("|")) {
                        let _match = innerProduct.name.match(/\|.+\|/g);
                        if (_match) {
                            _testVarianceName = _match.join('').replaceAll('|', '');
                            if (innerProduct.name.startsWith(productName.concat(` |${_testVarianceName}| `)) && !_variationProductIds.includes(innerProduct.id)) {
                                _varianceName = _testVarianceName;
                                let _variation = {
                                    parent_id: productId,
                                    id: innerProduct.id,
                                    name: productName.concat(' - ').concat(innerProduct.name.substring(productName.length + _varianceName.length + 4)),
                                    variationName: innerProduct.name.substring(productName.length + _varianceName.length + 4),
                                    price: innerProduct.price,
                                    vatRate: innerProduct.vatRate,
                                    stock: innerProduct.stock,
                                    rentalStock: innerProduct.rentalStock,
                                    description: innerProduct.description
                                };
                                _variations.push(_variation);
                                _variationProductIds.push(innerProduct.id);
                                if (!productDescription && innerProduct.description) {
                                    productDescription = innerProduct.description;
                                }
                                if (!productSubtitle && innerProduct.subtitle) {
                                    productSubtitle = innerProduct.subtitle;
                                }
                                if (productImage.includes('photoMissing') && !innerProduct.image.includes('photoMissing')) {
                                    productImage = innerProduct.image;
                                }
                            }
                        }
                    }
                });
                if (_variations.length > 0) {
                    if (_variations.length === 1) {
                        // treat the 1 variation as a single product, with a parsed name
                        product.name = `${productName} - ${_variations[0].name}`;
                        product.description = productDescription;
                        product.subtitle = productSubtitle;
                        product.image = productImage;
                    }
                    else {
                        // for now, just 1 variance
                        _variances.push({
                            id: varianceId,
                            name: _varianceName,
                            variations: _variations
                        });
                        product.id = _variations[0].id; // the virtual base product doesn't have an id, but we need one for caching reasons
                        product.name = productName;
                        product.subtitle = productSubtitle;
                        product.description = productDescription;
                        product.image = productImage;
                        varianceId++; // this is just a dummy id, but we need unique ids for the variances for memoization reasons
                    }
                }
                product.variances = _variances;
                _objects.push(product);
                if (!_virtualBaseProductIds.includes(productId)) {
                    _virtualBaseProductIds.push(productId);
                }
            });
            setProducts(_objects);
        }
    }, [productQuery.data, setProducts]);
    useEffect(() => {
        if (!enabled.product) {
            setEnabled(Object.assign(Object.assign({}, enabled), { product: true }));
        }
    }, []);
    useEffect(() => {
        if (enabled.product) {
            productQuery.refetch();
        }
    }, [enabled, selectedApptype]);
    const value = {};
    return (_jsx(WidgetContext.Provider, { value: value, children: children }));
};
export default WidgetProvider;
